import DigiobcanApi from '../../api/DigiobcanApi';
import { shortId } from '../../lib/ids';
import { createQueryFn, QueryNameSpace } from '../../lib/query';
import type { QueryFunctionContext } from '@tanstack/react-query';

type Params = {
  /** hľadaný text - názov, popis alebo identifikátor */
  q: string;
  /** `lon,lat[,radius]` - výsledky bližšie k výrezu mapy idú prvé */
  location?: string;
  limit?: number;
};

/**
 * Hľadanie podnetov pre panel nad mapou.
 *
 * Je to ten istý parameter `q` a tá istá SQL, akú má administrácia - občan
 * a úradník píšu do políčka to isté a nesmú dostať iné výsledky. Preto sa tu
 * nefiltruje nič navyše.
 *
 * Identifikátor sa skracuje na holý koncový segment. V databáze je uložené
 * celé URN (`urn:…:digital-obcan:pdnt:MQRJ73F`) a hľadá sa v ňom `LIKE`-om,
 * takže sedemznakový segment sa nájde vždy - kým appková podoba `pdnt:MQRJ73F`
 * by sa nenašla v odkaze z notifikácie a plné URN zase nie v tom, čo má
 * používateľ opísané z obrazovky. Skrátený segment je podreťazcom oboch.
 *
 * Odpoveď je **per nález**, nie per podnet, takže podnet so štyrmi
 * kategóriami sa vráti štyrikrát. V ponuke hľadania je to ten istý riadok
 * štyrikrát pod sebou, preto sa tu zlučuje - a preto sa aj pýta viac, než sa
 * nakoniec ukáže.
 */
export const searchReports = createQueryFn(
  QueryNameSpace.searchReports,
  async ({ queryKey }: QueryFunctionContext<[string] | [string, Params]>) => {
    const [, params] = queryKey;
    const term = params?.q?.trim() ?? '';

    const limit = params?.limit ?? 5;

    const data = await DigiobcanApi.getReports({
      q: term.includes(':') ? shortId(term) : term,
      location: params?.location,
      limit: limit * 4,
      orderBy: params?.location ? 'location' : 'date',
      orderDirection: params?.location ? 'asc' : 'desc',
    });

    const unique = new Map<string, (typeof data.reports)[number]>();
    data.reports.forEach((report) => {
      if (!unique.has(report.id)) unique.set(report.id, report);
    });

    return [...unique.values()].slice(0, limit);
  },
);
